import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Star, ExternalLink, Quote } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Reveal, RevealGroup, fadeUp } from '@/components/public/anim';
import { loadPlaces, hasGoogleMapsKey } from '@/lib/googleMaps';

/**
 * Recensioni Google Business Profile in Home (prima del footer) — Issue #10.
 *
 * Approccio REALE senza segreti server-side: usa la libreria Places di Google Maps JS
 * (stessa chiave `REACT_APP_GOOGLE_MAPS_API_KEY` gia' usata dalla mappa 3D) e l'endpoint
 * `PlacesService.getDetails` per leggere le recensioni verificate della scheda configurata.
 * Il `Place ID` si imposta dal CRM Admin → Contatti. Se manca ID o chiave, la sezione
 * NON viene mostrata (degrado silenzioso, nessun crash).
 */

const StarRow = ({ value = 0, size = 'h-4 w-4' }) => (
  <span className="inline-flex items-center gap-0.5" aria-label={`${value} su 5`}>
    {[0, 1, 2, 3, 4].map((i) => (
      <Star
        key={i}
        className={`${size} ${i < Math.round(value) ? 'fill-[hsl(var(--brand-sun))] text-[hsl(var(--brand-sun))]' : 'text-muted-foreground/40'}`}
      />
    ))}
  </span>
);

export function GoogleReviews() {
  const [state, setState] = useState('idle'); // idle | loading | done | hidden
  const [place, setPlace] = useState(null);

  useEffect(() => {
    let alive = true;
    let safety = null;
    if (!hasGoogleMapsKey()) { setState('hidden'); return undefined; }

    (async () => {
      // 1) Leggi il Place ID configurato dal CRM (contatti sito)
      let placeId = '';
      try {
        const { data } = await api.get('/cms/contacts');
        placeId = (data?.data?.google_place_id || '').trim();
      } catch (e) { /* noop */ }
      if (!placeId) { if (alive) setState('hidden'); return; }

      if (alive) setState('loading');
      // Guardia: se Google non risponde entro 12s, nascondi (niente skeleton infiniti).
      safety = setTimeout(() => { if (alive) setState((s) => (s === 'loading' ? 'hidden' : s)); }, 12000);
      // 2) Carica Places e leggi i dettagli con le recensioni
      try {
        const places = await loadPlaces();
        const svc = new places.PlacesService(document.createElement('div'));
        svc.getDetails(
          {
            placeId,
            fields: ['name', 'rating', 'user_ratings_total', 'reviews', 'url'],
          },
          (res, status) => {
            if (!alive) return;
            if (safety) clearTimeout(safety);
            const ok = status === (places.PlacesServiceStatus?.OK || 'OK');
            const reviews = (res && res.reviews) || [];
            if (ok && res && reviews.length) {
              setPlace({
                name: res.name,
                rating: res.rating,
                total: res.user_ratings_total,
                url: res.url,
                reviews: reviews.slice(0, 6),
              });
              setState('done');
            } else {
              setState('hidden');
            }
          }
        );
      } catch (e) {
        if (safety) clearTimeout(safety);
        if (alive) setState('hidden');
      }
    })();

    return () => { alive = false; if (safety) clearTimeout(safety); };
  }, []);

  if (state === 'hidden' || state === 'idle') return null;

  return (
    <section id="recensioni-google" className="sila-section scroll-mt-20 py-16 sm:py-24" data-testid="home-google-reviews">
      <Reveal>
        <div className="mx-auto max-w-2xl text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--brand-sun)/0.22)] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--brand-bark))]">
            <Star className="h-3.5 w-3.5 fill-current" />Recensioni verificate
          </span>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">Cosa dicono di noi su Google</h2>
          {state === 'done' && place && (
            <div className="mt-4 flex flex-col items-center gap-2">
              <div className="flex items-center gap-3">
                <span className="font-display text-4xl font-bold text-[hsl(var(--brand-bark))]" data-testid="google-reviews-rating">
                  {Number(place.rating || 0).toFixed(1)}
                </span>
                <div className="text-left">
                  <StarRow value={place.rating} size="h-5 w-5" />
                  <p className="text-sm text-muted-foreground" data-testid="google-reviews-total">
                    {place.total ? `${place.total} recensioni su Google` : 'Recensioni su Google'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </Reveal>

      {state === 'loading' && (
        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-48 w-full rounded-[24px]" />)}
        </div>
      )}

      {state === 'done' && place && (
        <>
          <RevealGroup className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {place.reviews.map((r, i) => (
              <motion.div key={i} variants={fadeUp} data-testid={`google-review-card-${i}`}>
                <Card className="h-full overflow-hidden rounded-[24px] border-border bg-card shadow-[var(--shadow-sm)] transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-[var(--shadow-md)]">
                  <CardContent className="flex h-full flex-col p-6">
                    <div className="flex items-center gap-3">
                      {r.profile_photo_url ? (
                        <img loading="lazy" src={r.profile_photo_url} alt={r.author_name}
                          className="h-11 w-11 rounded-full object-cover" referrerPolicy="no-referrer" />
                      ) : (
                        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 font-display text-lg font-semibold text-primary">
                          {(r.author_name || '?').charAt(0)}
                        </span>
                      )}
                      <div className="min-w-0">
                        <p className="truncate font-semibold leading-tight">{r.author_name}</p>
                        <span className="text-xs text-muted-foreground">{r.relative_time_description}</span>
                      </div>
                    </div>
                    <div className="mt-3"><StarRow value={r.rating} /></div>
                    <div className="relative mt-3 flex-1">
                      <Quote className="absolute -left-1 -top-1 h-5 w-5 text-primary/15" />
                      <p className="line-clamp-5 pl-4 text-sm leading-relaxed text-muted-foreground">{r.text}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </RevealGroup>

          {place.url && (
            <div className="mt-10 flex justify-center">
              <Button asChild variant="outline" className="rounded-full" data-testid="google-reviews-all-link">
                <a href={place.url} target="_blank" rel="noreferrer">
                  Vedi tutte le recensioni su Google <ExternalLink className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

export default GoogleReviews;
